import { useState } from 'react'
import { Prohibit, PencilSimple, CheckCircle } from '@phosphor-icons/react'
import { useI18n } from '@/i18n'
import { newId } from '@/lib/ids'
import { formatGrams } from '@/lib/weight'
import { Badge, Banner, Button, ChoiceGroup, Field, PhotoCapture, PickerSheet, Sheet } from '@/ui'
import type { TagEntry } from '../../commands'
import { ActionBar, BigFigure } from '../../components/GateScreen'
import { GuardedScanButton, PlainScanButton } from '../../components/Scan'
import { useCodeLabel } from '../../components/labels'
import { guardFor } from '../../data/useGate'
import { bagNet } from '../../model/figures'
import { checkCode } from '../../model/scanGuard'
import {
  bagsOfSlot,
  bagWeight,
  draftPayloads,
  lotPlan,
  nextStep,
  slotOf,
  tagPhase,
} from '../../model/delivery'
import { useWizard } from './context'
import { WeightField } from './WeightEntry'
import { useWeightEntry } from './hooks'

function useSackOptions() {
  const { roster } = useWizard()
  return roster.sackTypes.map((s) => ({
    value: s.id,
    label: `${s.name}`,
    description: s.code,
    meta: <span className="font-mono text-[13px] text-muted">{formatGrams(s.tare_g)}</span>,
  }))
}

/** F4.6 — टैग और वज़न · Tag and weigh: one tag, one sack type, one typed weight per bag. */
export function StepTag() {
  const { t } = useI18n()
  const w = useWizard()
  const { d, dispatch, roster, session, used, stamp, startDispute } = w
  const sackOptions = useSackOptions()
  const phase = tagPhase(d)
  const [scanned, setScanned] = useState<{ payload: string; entry: TagEntry } | null>(null)
  const [sack, setSack] = useState<string | null>(
    d.last_sack_type_id ?? roster.sackTypes[0]?.id ?? null,
  )
  const weight = useWeightEntry(roster.config.weight_min_g, roster.config.weight_max_g)
  const [scalePhoto, setScalePhoto] = useState<string | null>(null)
  const [sheet, setSheet] = useState<null | 'fix' | 'void'>(null)
  const photoRequired = d.reading_photos_required === true

  const sackType = roster.sackTypes.find((s) => s.id === sack) ?? null
  const net = weight.value !== null && sackType ? weight.value - sackType.tare_g : null
  const plan = lotPlan(d)
  const currentSlot = phase.phase === 'RED' ? slotOf(d, { red: true }) : 'A'
  const lot = plan.find((l) => l.slot === currentSlot) ?? plan[0]!
  const lotBags = bagsOfSlot(d, lot.slot).filter((b) => !b.withdrawn)
  const running = lotBags.reduce((s, b) => s + bagNet(b), 0)
  const guard = guardFor(session, roster, used, 'BAG', draftPayloads(d))
  const canSave =
    phase.phase !== 'DONE' &&
    !!scanned &&
    !!sackType &&
    weight.value !== null &&
    weight.twoDecimals &&
    (!photoRequired || !!scalePhoto) &&
    net !== null &&
    net > 0

  const save = () => {
    if (!scanned || !sackType || weight.value === null) return
    if (photoRequired && !scalePhoto) return
    if (weight.askAgain()) return
    if (!weight.ready) return
    const at = stamp()
    dispatch({
      type: 'tag_bag',
      bag: {
        id: newId(),
        qr_payload: scanned.payload,
        entry: scanned.entry,
        sack_type_id: sackType.id,
        tare_g: sackType.tare_g,
        weights: [
          {
            id: newId(),
            gross_g: weight.value,
            taken_at: at,
            confirmed_twice: weight.confirmedTwice,
            supersedes_id: null,
            photo_media_id: scalePhoto,
          },
        ],
        tagged_at: at,
      },
    })
    setScanned(null)
    setScalePhoto(null)
    weight.reset()
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-semibold">{d.farmer.name}</div>
          <div className="font-mono text-[13px] text-muted">{lot.code}</div>
        </div>
        <Badge tone={phase.phase === 'RED' ? 'danger' : 'ok'}>
          {t(phase.phase === 'RED' ? 'gate.lot.redBagsShort' : 'gate.lot.goodBagsShort')}
        </Badge>
      </div>

      <BigFigure label={t('gate.tag.runningTotal')} value={formatGrams(running)} tone="ok" />

      {phase.phase === 'DONE' ? (
        <Banner tone="success" title={t('gate.tag.allDone')}>
          {plan.map((l) => `${l.code}: ${l.bag_count}`).join(' · ')}
        </Banner>
      ) : (
        <div className="flex flex-col gap-3 rounded-card border border-ink/20 bg-surface p-3">
          <div className="text-[18px] font-semibold">
            {t('gate.tag.bagNofM', { n: phase.index, of: phase.of })}
          </div>
          {phase.phase === 'RED' && <Banner tone="warn">{t('gate.tag.redPhase')}</Banner>}
          {scanned ? (
            <div className="flex items-center gap-2 rounded-chip border border-ok/30 bg-ok-wash px-3 py-2">
              <CheckCircle className="text-ok" weight="fill" aria-hidden="true" />
              <span className="flex-1 font-mono text-[16px]">{scanned.payload}</span>
              {scanned.entry === 'TYPED' && <Badge tone="warn">{t('gate.tag.typed')}</Badge>}
              <Button size="sm" variant="ghost" onClick={() => setScanned(null)}>
                {t('gate.change')}
              </Button>
            </div>
          ) : (
            <GuardedScanButton
              guard={guard}
              label={t('gate.tag.scanTag')}
              onAccept={(payload, entry) => setScanned({ payload, entry })}
              onRefuse={(scan) => dispatch({ type: 'refuse_scan', scan })}
            />
          )}
          <Field label={t('gate.tag.sackType')} required>
            <PickerSheet
              title={t('gate.tag.sackType')}
              value={sack}
              onChange={setSack}
              options={sackOptions}
            />
          </Field>
          <WeightField
            entry={weight}
            label={t('gate.tag.weight')}
            hint={t('gate.tag.weightHint')}
          />
          {d.capture_mode === 'APP' && (
            <Field label={t('gate.v1.weigh.scalePhoto')} required={photoRequired}>
              <PhotoCapture value={scalePhoto} onChange={setScalePhoto} />
            </Field>
          )}
          {net !== null && (
            <div className="flex justify-between text-[15px]">
              <span className="text-muted">{t('gate.tag.produce')}</span>
              <span className={`font-mono ${net <= 0 ? 'text-danger' : ''}`}>
                {formatGrams(net)}
              </span>
            </div>
          )}
          <Button size="xl" fullWidth disabled={!canSave} onClick={save}>
            {t('gate.tag.saveNext')}
          </Button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <Button
          size="touch"
          variant="secondary"
          leadingIcon={<PencilSimple aria-hidden="true" />}
          disabled={d.bags.length === 0}
          onClick={() => setSheet('fix')}
        >
          {t('gate.tag.fixLast')}
        </Button>
        <Button
          size="touch"
          variant="secondary"
          leadingIcon={<Prohibit aria-hidden="true" />}
          onClick={() => setSheet('void')}
        >
          {t('gate.tag.voidTag')}
        </Button>
      </div>

      {d.bags.length > 0 && (
        <div>
          <div className="mb-1 text-[13px] font-semibold">{t('gate.tag.bagsDone')}</div>
          <ul className="flex flex-col divide-y divide-rule/70 rounded-card border border-rule bg-surface">
            {d.bags.map((b) => {
              const cur = bagWeight(b)
              const sackT = roster.sackTypes.find((s) => s.id === b.sack_type_id)
              return (
                <li
                  key={b.id}
                  className={`flex items-center gap-2 px-3 py-2 ${b.withdrawn ? 'opacity-50 line-through' : ''}`}
                >
                  <span
                    className={`h-2.5 w-2.5 shrink-0 rounded-full ${b.red ? 'bg-danger' : 'bg-ok'}`}
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-mono text-[14px]">{b.qr_payload}</span>
                    <span className="block text-[12px] text-muted">
                      {sackT?.code ?? b.sack_type_id} · {cur ? formatGrams(cur.gross_g) : '—'}
                      {b.weights.length > 1 ? ` · ${t('gate.tag.reweighed')}` : ''}
                    </span>
                  </span>
                  <span className="font-mono text-[14px]">{formatGrams(bagNet(b))}</span>
                  {!b.withdrawn && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => startDispute({ about: 'WEIGHT', container_id: b.id })}
                    >
                      {t('gate.dispute.button')}
                    </Button>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      )}

      <ActionBar>
        <Button
          size="xl"
          fullWidth
          disabled={phase.phase !== 'DONE'}
          onClick={() => dispatch({ type: 'goto', step: nextStep(d, 'tag') })}
        >
          {phase.phase === 'DONE'
            ? t('common.next')
            : t('gate.tag.moreBags', { count: phase.of - phase.index + 1 })}
        </Button>
      </ActionBar>

      {sheet === 'fix' && <FixLastSheet onClose={() => setSheet(null)} />}
      {sheet === 'void' && <VoidInDraftSheet onClose={() => setSheet(null)} />}
    </div>
  )
}

function FixLastSheet({ onClose }: { onClose: () => void }) {
  const { t } = useI18n()
  const { d, dispatch, roster, stamp } = useWizard()
  const last = d.bags.at(-1)
  const sackOptions = useSackOptions()
  const [sack, setSack] = useState<string | null>(last?.sack_type_id ?? null)
  const weight = useWeightEntry(roster.config.weight_min_g, roster.config.weight_max_g)
  const [scalePhoto, setScalePhoto] = useState<string | null>(null)
  if (!last) return null
  const sackType = roster.sackTypes.find((s) => s.id === sack)
  const cur = bagWeight(last)
  return (
    <Sheet
      open
      onClose={onClose}
      title={t('gate.tag.fixLast')}
      description={`${last.qr_payload} · ${cur ? formatGrams(cur.gross_g) : ''}`}
      footer={
        <Button
          size="touch"
          fullWidth
          disabled={
            !sackType ||
            weight.value === null ||
            !weight.twoDecimals ||
            (d.reading_photos_required === true && !scalePhoto)
          }
          onClick={() => {
            if (!sackType || weight.value === null) return
            if (d.reading_photos_required === true && !scalePhoto) return
            if (weight.askAgain() || !weight.ready) return
            dispatch({
              type: 'fix_last_bag',
              weight: {
                id: newId(),
                gross_g: weight.value,
                taken_at: stamp(),
                confirmed_twice: weight.confirmedTwice,
                supersedes_id: null,
                photo_media_id: scalePhoto,
              },
              sack:
                sackType.id !== last.sack_type_id
                  ? { sack_type_id: sackType.id, tare_g: sackType.tare_g }
                  : undefined,
            })
            onClose()
          }}
        >
          {t('common.save')}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t('gate.tag.sackType')} required>
          <PickerSheet
            title={t('gate.tag.sackType')}
            value={sack}
            onChange={setSack}
            options={sackOptions}
          />
        </Field>
        <WeightField entry={weight} label={t('gate.tag.newWeight')} />
        {d.capture_mode === 'APP' && (
          <Field
            label={t('gate.v1.weigh.scalePhoto')}
            required={d.reading_photos_required === true}
          >
            <PhotoCapture value={scalePhoto} onChange={setScalePhoto} />
          </Field>
        )}
        <p className="text-[13px] text-muted">{t('gate.tag.fixNote')}</p>
      </div>
    </Sheet>
  )
}

/** Void tag inside the delivery: a torn tag, or the tag on a bag (that bag must be tagged and weighed again). */
export function VoidInDraftSheet({
  onClose,
  kind = 'BAG',
}: {
  onClose: () => void
  kind?: 'BAG' | 'SEAL'
}) {
  const { t } = useI18n()
  const { d, dispatch, stamp, session, roster, used } = useWizard()
  const code = useCodeLabel()
  const [reason, setReason] = useState<'TORN' | 'MISPRINT' | 'OTHER'>('TORN')
  const inDraft = draftPayloads(d)
  const guard = guardFor(session, roster, used, kind)
  return (
    <Sheet
      open
      onClose={onClose}
      title={t(kind === 'BAG' ? 'gate.tag.voidTag' : 'gate.sample.voidSeal')}
    >
      <div className="flex flex-col gap-4">
        <Field label={t('gate.tag.reason')} required>
          <ChoiceGroup
            options={(['TORN', 'MISPRINT', 'OTHER'] as const).map((r) => ({
              value: r,
              label: code(r),
            }))}
            value={reason}
            onChange={setReason}
          />
        </Field>
        <PlainScanButton
          label={t('gate.tag.scanToVoid')}
          validate={(c) => {
            if (inDraft.has(c)) return null
            return checkCode(c, guard).ok ? null : t('gate.tag.notYours')
          }}
          onScan={(payload) => {
            dispatch({ type: 'void_tag', payload, kind, reason, at: stamp() })
            onClose()
          }}
        />
        <p className="text-[13px] text-muted">{t('gate.tag.keepCut')}</p>
      </div>
    </Sheet>
  )
}
